import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import api from '@/utils/api';

export default function Assessment() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    initializeAssessment();
  }, [token]);

  const initializeAssessment = async () => {
    try {
      // Validate token first
      const candidateRes = await api.post('/invites/validate', null, {
        params: { token }
      });
      setCandidate(candidateRes.data);

      // Load questions for this specific candidate
      const questionsRes = await api.get(`/questions/candidate/${candidateRes.data.id}`);
      setQuestions(questionsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar avaliação');
      navigate(`/avaliacao/${token}`);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentAnswer = answers[currentQuestion?.id];

  const saveAnswer = async (questionId, option) => {
    try {
      await api.post('/answers', {
        candidate_id: candidate.id,
        question_id: questionId,
        chosen: option
      });
    } catch (error) {
      console.error('Error saving answer:', error);
    }
  };

  const handleAnswer = (option) => {
    if (currentAnswer === option) return; // evita disparo duplicado (card + radio no mesmo clique)
    const newAnswers = { ...answers, [currentQuestion.id]: option };
    setAnswers(newAnswers);
    saveAnswer(currentQuestion.id, option);
  };

  const handleNext = () => {
    if (!currentAnswer) {
      toast.error('Por favor, selecione uma resposta');
      return;
    }

    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < questions.length) {
      toast.error(`Você respondeu ${answeredCount}/${questions.length} perguntas. Complete todas antes de finalizar.`);
      return;
    }

    setSubmitting(true);
    try {
      // Garante que a última resposta selecionada já foi persistida antes de finalizar,
      // evitando que o /submit rode antes do POST /answers (disparado sem aguardar) concluir.
      if (currentAnswer) {
        await saveAnswer(currentQuestion.id, currentAnswer);
      }
      await api.post(`/submit?candidate_id=${candidate.id}`);
      toast.success('Avaliação concluída com sucesso!');
      navigate(`/avaliacao/${token}/completo`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando perguntas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header with Progress */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Questão {currentIndex + 1} de {questions.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {Math.round(progress)}% completo
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Tempo estimado: {Math.ceil((questions.length - currentIndex - 1) * 0.35)} min
              </p>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="shadow-premium border-0 glass mb-6 animate-slide-in" data-testid="question-card">
          <CardContent className="pt-8 pb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 leading-relaxed" style={{fontFamily: 'Manrope'}}>
              {currentQuestion?.prompt}
            </h2>

            <RadioGroup value={currentAnswer} onValueChange={handleAnswer}>
              <div className="space-y-4">
                {['A', 'B', 'C', 'D'].map((option) => (
                  <div
                    key={option}
                    className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 ${
                      currentAnswer === option
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 bg-white'
                    }`}
                    data-testid={`option-${option.toLowerCase()}`}
                  >
                    <RadioGroupItem value={option} id={`option-${option}`} className="mt-1" />
                    <Label htmlFor={`option-${option}`} className="flex-1 cursor-pointer text-base leading-relaxed">
                      {currentQuestion?.[`option_${option.toLowerCase()}`]}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Tip Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-amber-900 text-sm">
            <strong>💡 Dica:</strong> Responda o que você faria <strong>na prática</strong>, não o que você acha que esperam de você.
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            data-testid="previous-button"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>

          <div className="text-sm text-gray-500">
            {Object.keys(answers).length}/{questions.length} respondidas
          </div>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !currentAnswer}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              data-testid="submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Finalizar
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!currentAnswer}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700"
              data-testid="next-button"
            >
              Próxima
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
